import {Alert} from 'src/design/atoms/Alert';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconArrowLeft} from '@tabler/icons-react';
import React from 'react';
import type {InspectionResponse} from 'src/api/ProactApiClient';
import {GeoStatusBanner} from 'src/app/inspections/geo/GeoStatusBanner';
import {SiteLocationMap} from 'src/app/inspections/geo/SiteLocationMap';
import {useGeoStatus} from 'src/app/inspections/geo/useGeoStatus';
import {Button} from 'src/design/atoms/Button';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {useTransitionInspectionMutation} from 'src/redux/queries/inspection';
import {useAlert} from 'src/shared/hooks/useAlert';
import type {GeoCoordinates} from 'src/shared/utils/geoDistance';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface CompletionConfirmPanelProps {
  inspection: InspectionResponse;
  site?: GeoCoordinates;
  onBack: () => void;
}

/**
 * Completion screen for the guided execute flow. Geo never blocks; on success the cache
 * invalidation flips state to Completed and the parent swaps screens — no navigation here.
 */
export const CompletionConfirmPanel: React.FC<CompletionConfirmPanelProps> = ({
  inspection,
  site,
  onBack,
}) => {
  const {setAlert} = useAlert();
  const [transitionInspection, {isLoading: isCompleting}] =
    useTransitionInspectionMutation();
  const geo = useGeoStatus(true, site);

  const handleComplete = async () => {
    try {
      await transitionInspection({
        id: inspection.id,
        request: {
          transition: 'complete',
          latitude: geo.latitude,
          longitude: geo.longitude,
          geoNote: geo.geoNote,
        },
      }).unwrap();
      // No navigate — state flips to Completed, the page's derived branch
      // renders the review screen.
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Stack gap="md">
      <Button
        variant="subtle"
        color="black"
        size="sm"
        px={4}
        leftSection={<IconArrowLeft size={16} />}
        onClick={onBack}
        style={{alignSelf: 'flex-start'}}>
        Back to devices
      </Button>

      <Stack gap={2}>
        <Text size="lg" fw={600}>
          Complete inspection — review results
        </Text>
        <Text size="sm" c="dimmed">
          Review the results below, then confirm to complete this inspection.
        </Text>
      </Stack>

      <Alert color="green" variant="light">
        All {inspection.scopedDeviceCount} devices have a result
      </Alert>

      <SimpleGrid cols={3}>
        <InlineStat label="Pass" value={inspection.passCount} />
        {/* Fail count is labeled "Failed devices", never "Deficiencies". */}
        <InlineStat label="Failed devices" value={inspection.failCount} />
        <InlineStat label="No access" value={inspection.noAccessCount} />
      </SimpleGrid>

      <GeoStatusBanner
        status={geo.status}
        distanceFeet={geo.distanceFeet}
        capturing={geo.isCapturing}
      />

      <SiteLocationMap
        site={site}
        user={
          geo.latitude !== undefined && geo.longitude !== undefined
            ? {latitude: geo.latitude, longitude: geo.longitude}
            : undefined
        }
        capturing={geo.isCapturing}
      />

      <Button
        fullWidth
        size="lg"
        color="yellow"
        loading={isCompleting}
        onClick={handleComplete}>
        Complete inspection
      </Button>
    </Stack>
  );
};
